import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTable } from "react-table";
import HeaderMenu from "../Header/HeaderMenu";
import CreateNotification from "../Notification/CreateNotification";
import axiosApi from "../Utils/axiosApi";
import checkPermission from "../Utils/checkPermission";
import {
  Pagination
} from "@mui/material";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  MenuItem,
  Typography,
  TextField,
  Grid,
  Box,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";

const NotificationList = () => {
  const [notifications, setNotifications] = useState([]);
  const [hostelId, setHostelId] = useState("");

  const [hostelerList, setHostelerList] = useState([]);
  const [selectedHosteler, setSelectedHosteler] = useState("");
  const [loadingHostelers, setLoadingHostelers] = useState(false);

  const [availableHostels, setAvailableHostels] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [tabValue, setTabValue] = useState(0);

const [pageNo, setPageNo] = useState(1);
const [pageSize, setPageSize] = useState(20);


const [totalPages, setTotalPages] = useState(0);

  const [messageDialog, setMessageDialog] = useState({
    open: false,
    text: "",
  });

  /* ---------------- FETCH HOSTELS ---------------- */
  useEffect(() => {
    axiosApi
      .post("/hostel/getHostelName", {})
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.hostels || [];

        const formatted = data.map(([id, name]) => ({ id, name }));
        setAvailableHostels(formatted);
      })
      .catch((error) => console.error("Error fetching hostels:", error));
  }, []);

  /* ---------------- FETCH HOSTELERS ---------------- */
  const fetchHostelers = async (hostelId) => {
    try {
      setLoadingHostelers(true);

      const response = await axiosApi.post("/hosteler/getHosteler", {
        hostelIds: [Number(hostelId)],
      });

      const data = Array.isArray(response.data) ? response.data : [];

      const normalized = data.map((h) => ({
        id: String(h.hostelerId || h.id || h[0]),
        hostlerName:
          h.hostlerName ||
          h.hostelerName ||
          `${h.hostlerFirstName || ""} ${
            h.hostlerLastName || ""
          }`.trim(),
      }));

      setHostelerList(normalized);
    } catch (err) {
      console.error("Error fetching hostelers:", err);
      setHostelerList([]);
    } finally {
      setLoadingHostelers(false);
    }
  };

  useEffect(() => {
    if (hostelId) {
      fetchHostelers(hostelId);
      setSelectedHosteler("");
    }
  }, [hostelId]);

  /* ---------------- FETCH NOTIFICATIONS ---------------- */
  const fetchNotifications = useCallback(async () => {
    setLoading(true);

    try {
      const payload = {
  recipientHostelId: hostelId ? [Number(hostelId)] : [],
  recipientHostelerId: selectedHosteler
    ? [Number(selectedHosteler)]
    : [],
  directionType:
    tabValue === 1 ? "SENT" : "RECEIVED", // use whatever value backend expects
  pageNo,
  pageSize,
  tabValue,
};

      const response = await axiosApi.post(
        "/hostelNotification/getHostelNotification",
        payload
      );

setNotifications(response.data?.notifications || []);


setTotalPages(
  response.data.totalPages || 0
);
if ((response.data?.totalPages || 0) === 0) {
  setPageNo(1);
}
      setError(null);
    } catch (err) {
      console.error(err);

      setError(
        err?.response?.data?.message ||
          "Failed to load notifications."
      );
    } finally {
      setLoading(false);
    }
  }, [hostelId, selectedHosteler,pageNo,
  pageSize,tabValue]);

useEffect(() => {
  fetchNotifications();
}, [fetchNotifications]);
 

  /* ---------------- VIEW DETAILS ---------------- */
  const handleViewDetails = useCallback(async (notification) => {
    try {
      const response = await axiosApi.post(
        "/hostelNotification/getHostelNotification",
        {
          hostelNotificationIds: [notification.id],
        }
      );

      const fullNotification =
  response.data?.notifications?.[0] || notification;

      setSelectedNotification(fullNotification);
      setIsUpdate(true);
      setOpen(true);
    } catch (err) {
      console.error(
        "Error fetching notification details:",
        err
      );
    }
  }, []);

  /* ---------------- CREATE ---------------- */
  const handleCreate = () => {
    setSelectedNotification(null);
    setIsUpdate(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedNotification(null);
    setIsUpdate(false);
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = (notificationId) => {
    setNotificationToDelete(notificationId);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!notificationToDelete) return;

    try {
      await axiosApi.post(
        "/hostelNotification/deleteHostelNotification",
        {
          hostelNotificationIds: [notificationToDelete],
        }
      );

      setSnackbarMessage(
        "Notification deleted successfully"
      );

      setSnackbarOpen(true);

      fetchNotifications();
    } catch (error) {
      console.error(
        "Error deleting notification:",
        error
      );

      setSnackbarMessage(
        "Failed to delete notification"
      );

      setSnackbarOpen(true);
    } finally {
      setConfirmDeleteOpen(false);
      setNotificationToDelete(null);
    }
  };

  /* ---------------- TABLE COLUMNS ---------------- */
  const columns = useMemo(
    () => [
      {
  Header: "No.",
  Cell: ({ row }) =>
    (pageNo - 1) * pageSize + row.index + 1,
},

      {
        Header: "Notification Title",
        accessor: "title",
      },

      {
        Header: "Notification Message",
        accessor: "message",

        Cell: ({ value }) => {
          if (!value) return "N/A";

          const maxLength = 30;

          const truncated =
            value.length > maxLength
              ? value.substring(0, maxLength) + "..."
              : value;

          const isMobile =
            window.innerWidth < 768;

          if (isMobile) {
            return (
              <span
                style={{
                  cursor: "pointer",
                  color: "#1976d2",
                }}
                onClick={() =>
                  setMessageDialog({
                    open: true,
                    text: value,
                  })
                }
              >
                {truncated}
              </span>
            );
          }

          return (
            <Tooltip title={value} arrow>
              <span>{truncated}</span>
            </Tooltip>
          );
        },
      },

      {
        Header:
          tabValue === 0
            ? "Received From"
            : "Sent By",

        accessor: "initiatorUserName",

        Cell: ({ value }) => value || "N/A",
      },

      {
        Header: "Notification Type",
        accessor: "type",
      },

      {
        Header: "Date & Time",
        accessor: "createdDateTime",
      },

      {
        Header: "Actions",

        Cell: ({ row }) => {
          const canDelete =
            row.original.directionType ===
              "SENT" ||
            (row.original.directionType !==
              "SENT" &&
              checkPermission(
                "Notification",
                "Notifications",
                "SPECIAL_DELETE"
              ));

          return (
            <div
              style={{
                display: "flex",
                gap: "8px",
              }}
            >
              <Button
                disabled={
                  !checkPermission(
                    "Notification",
                    "Notifications",
                    "VIEW"
                  )
                }
                size="small"
                variant="outlined"
                color="primary"
                onClick={() =>
                  handleViewDetails(row.original)
                }
              >
                View
              </Button>

              <Tooltip
                title={
                  canDelete
                    ? "Delete this notification"
                    : "You do not have permission"
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() =>
                      handleDelete(row.original.id)
                    }
                    disabled={!canDelete}
                  >
                    Delete
                  </Button>
                </span>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [handleViewDetails, tabValue, pageNo,
  pageSize]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
   data: notifications,
  });

  return (
    <>
      <HeaderMenu />

      <div className="notification-list">
        <h2>Notifications</h2>

        {/* ---------------- TABS ---------------- */}
<Box
  sx={{
    mb: 3,
    borderBottom: "1px solid #e0e0e0",
    overflowX: "auto",
  }}
>
  <Tabs
    value={tabValue}
    onChange={(e, newValue) => setTabValue(newValue)}
    variant="fullWidth"
    scrollButtons="auto"
    allowScrollButtonsMobile
    sx={{
      minHeight: 48,

      "& .MuiTabs-flexContainer": {
        gap: {
          xs: 1,
          sm: 2,
        },
      },

      "& .MuiTab-root": {
        textTransform: "none",
        fontWeight: 600,
        fontSize: {
          xs: "0.85rem",
          sm: "0.95rem",
        },
        minHeight: 48,
        borderRadius: "10px 10px 0 0",
        transition: "all 0.2s ease",
        px: {
          xs: 1,
          sm: 3,
        },
      },

      "& .Mui-selected": {
        color: "#1976d2 !important",
        backgroundColor: "rgba(25, 118, 210, 0.08)",
      },

      "& .MuiTabs-indicator": {
        height: 3,
        borderRadius: 3,
      },
    }}
  >
    <Tab
      label={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <span>📥</span>
          <span>Inbox</span>
        </Box>
      }
    />

    <Tab
      label={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <span>📤</span>
          <span>Sent</span>
        </Box>
      }
    />
  </Tabs>
</Box>

        {/* ---------------- FILTERS ---------------- */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Filter by Property"
                value={hostelId}
                onChange={(e) =>
                  setHostelId(e.target.value)
                }
                fullWidth
                size="small"
              >
                {availableHostels.map((hostel) => (
                  <MenuItem
                    key={hostel.id}
                    value={hostel.id}
                  >
                    {hostel.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Filter by Hosteler"
                value={selectedHosteler}
                onChange={(e) =>
                  setSelectedHosteler(
                    e.target.value
                  )
                }
                fullWidth
                size="small"
                disabled={!hostelId}
              >
                <MenuItem value="">
                  <em>All Hostelers</em>
                </MenuItem>

                {loadingHostelers ? (
                  <MenuItem disabled>
                    Loading...
                  </MenuItem>
                ) : (
                  hostelerList.map((h) => (
                    <MenuItem
                      key={h.id}
                      value={h.id}
                    >
                      {h.hostlerName}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Button
  variant="contained"
  fullWidth
  onClick={() => {
    setPageNo(1);
    fetchNotifications();
  }}
>
  Search
</Button>
            </Grid>
          </Grid>
        </Box>

        {/* ---------------- CREATE BUTTON ---------------- */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "1rem",
          }}
        >
          <Button
            disabled={
              !checkPermission(
                "Notification",
                "Notifications",
                "CREATE"
              )
            }
            variant="contained"
            onClick={handleCreate}
          >
            + Create Notification
          </Button>
        </div>

        {/* ---------------- TABLE ---------------- */}
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p style={{ color: "red" }}>{error}</p>
        ) : (
          <div
  className="table-container"
  style={{
    width: "100%",
    overflowX: "auto",
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    background: "#fff",
  }}
>
            <table
              {...getTableProps()}
              className="table"
            >
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                  >
                    {headerGroup.headers.map(
                      (column) => (
                        <th
                          {...column.getHeaderProps()}
                        >
                          {column.render("Header")}
                        </th>
                      )
                    )}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);

                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          data-label={
                            cell.column.Header
                          }
                        >
                          {cell.render("Cell") ||
                            "N/A"}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
			
			<div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "16px",
  }}
>
  <Pagination
    count={Math.max(totalPages, 1)}
    page={pageNo}
    onChange={(e, value) => setPageNo(value)}
    color="primary"
    shape="rounded"
  />

  <div>
    <label
      htmlFor="rows-per-page"
      style={{ marginRight: "8px" }}
    >
      Rows per page:
    </label>

    <select
      id="rows-per-page"
      value={pageSize}
      onChange={(e) => {
        setPageSize(Number(e.target.value));
        setPageNo(1);
      }}
    >
      {[5, 10, 15, 20].map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </div>
</div>
          </div>
        )}

        {/* ---------------- CREATE / EDIT DIALOG ---------------- */}
        <Dialog
          open={open}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {isUpdate
              ? "Edit Notification"
              : "Create Notification"}
          </DialogTitle>

          <DialogContent>
            <CreateNotification
              isDialog={true}
              isUpdate={isUpdate}
              initialData={selectedNotification}
              onSuccess={() => {
                setSnackbarMessage(
                  isUpdate
                    ? "Notification updated!"
                    : "Notification created!"
                );

                setSnackbarOpen(true);

                fetchNotifications();
              }}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ---------------- DELETE DIALOG ---------------- */}
        <Dialog
          open={confirmDeleteOpen}
          onClose={() =>
            setConfirmDeleteOpen(false)
          }
        >
          <DialogTitle>
            Confirm Delete
          </DialogTitle>

          <DialogContent>
            <Typography>
              Are you sure you want to delete
              this notification?
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() =>
                setConfirmDeleteOpen(false)
              }
            >
              Cancel
            </Button>

            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* ---------------- MESSAGE DIALOG ---------------- */}
        <Dialog
          open={messageDialog.open}
          onClose={() =>
            setMessageDialog({
              open: false,
              text: "",
            })
          }
          fullWidth
        >
          <DialogTitle>Message</DialogTitle>

          <DialogContent>
            <Typography>
              {messageDialog.text}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() =>
                setMessageDialog({
                  open: false,
                  text: "",
                })
              }
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ---------------- SNACKBAR ---------------- */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() =>
            setSnackbarOpen(false)
          }
          message={snackbarMessage}
        />
      </div>
    </>
  );
};

export default NotificationList;